import React, { useMemo, useState } from "react";
import Reveal from "./Reveal";
import BrochureGate from "./BrochureGate";
import { CERTIFICATIONS, CHANNEL_LABEL } from "../lib/certifications";

const CHANNELS = [
  { id: "all", label: "All" },
  { id: "school", label: "Schools" },
  { id: "college", label: "Colleges" },
  { id: "corporate", label: "Corporates" },
  { id: "individual", label: "Individuals" },
];

export default function Certifications() {
  const [active, setActive] = useState("all");

  const visible = useMemo(() => {
    if (active === "all") return CERTIFICATIONS;
    return CERTIFICATIONS.filter((c) => c.channels.includes(active));
  }, [active]);

  return (
    <section
      id="certifications"
      className="py-10 md:py-14 border-t hairline"
      style={{ background: "var(--canvas-deep)" }}
      data-testid="certifications-section"
    >
      <div className="container-edge">
        <Reveal>
          <p className="section-number">09 — THE CERTIFICATIONS</p>
        </Reveal>
        <Reveal>
          <h2
            className="mt-6 font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[0.95] tracking-tight max-w-[920px]"
            style={{ fontWeight: 500 }}
          >
            11 Launch Certifications.
            <br />
            <em className="italic grad-brand-text" style={{ fontWeight: 500 }}>
              Built for who you are. Scoped to where you're going.
            </em>
          </h2>
        </Reveal>

        {/* Channel filter */}
        <Reveal delay={140}>
          <div
            className="mt-8 flex gap-2 overflow-x-auto pb-2 -mx-2 px-2"
            role="tablist"
            aria-label="Channel filter"
            data-testid="channel-filter"
          >
            {CHANNELS.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={active === c.id}
                onClick={() => setActive(c.id)}
                className={`filter-pill whitespace-nowrap ${active === c.id ? "is-active" : ""}`}
                data-testid={`channel-pill-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>

        {/* Cards grid */}
        <div
          className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-soft)] border hairline"
          data-testid="cert-grid"
        >
          {visible.map((c, i) => (
            <Reveal key={c.sku} delay={Math.min(i, 5) * 50}>
              <article
                className="card-edge h-full p-8 flex flex-col"
                style={{ borderRadius: 0, borderTop: 0, borderLeft: 0, borderRight: 0, borderBottom: 0 }}
                data-testid={`cert-card-${c.sku}`}
              >
                <header className="flex items-start justify-between gap-4">
                  <p className="mono-label-gold">{c.sku}</p>
                  <div className="flex flex-wrap gap-1.5 justify-end">
                    {c.tiers.map((t) => (
                      <span
                        key={t}
                        className="text-[10px] font-mono px-2 py-0.5 rounded-sm border hairline-strong"
                        style={{ color: "var(--ink)", letterSpacing: "0.08em" }}
                      >
                        {t.toUpperCase()}
                      </span>
                    ))}
                  </div>
                </header>

                <h3
                  className="mt-5 font-display text-[26px] leading-[1.15] tracking-tight"
                  style={{ fontWeight: 500 }}
                >
                  {c.title}
                </h3>

                <p
                  className="mt-3 italic text-[14px] leading-relaxed"
                  style={{ color: "var(--ink-secondary)" }}
                >
                  {c.audience}
                </p>

                <p className="mt-4 font-mono text-[12px]" style={{ letterSpacing: "0.12em", color: "var(--ink-tertiary)" }}>
                  {c.hours.toUpperCase()} · {c.pillar}
                </p>

                <p
                  className="mt-5 italic text-[14px] leading-relaxed"
                  style={{ color: "var(--ink)" }}
                >
                  "{c.outcome}"
                </p>

                <div className="mt-5 pt-5 border-t hairline">
                  <p className="mono-label mb-2">YOU'LL SHIP</p>
                  <p className="text-[13px] leading-relaxed" style={{ color: "var(--ink-secondary)" }}>
                    {c.capstone}
                  </p>
                </div>

                <div className="mt-auto pt-6 border-t hairline">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-1.5">
                      {c.channels.map((ch) => (
                        <span
                          key={ch}
                          className="text-[10px] font-mono px-1.5 py-0.5"
                          style={{ color: "var(--ink-tertiary)", letterSpacing: "0.08em", border: "1px solid var(--border-soft)" }}
                        >
                          {CHANNEL_LABEL[ch]}
                        </span>
                      ))}
                    </div>
                    <a
                      href="#enquiry"
                      className="text-[13px] link-underline text-ink whitespace-nowrap"
                      data-testid={`cert-enquire-${c.sku}`}
                    >
                      Enquire →
                    </a>
                  </div>
                  <div className="mt-3">
                    <BrochureGate sku={c.sku} title={c.title} />
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <p
            className="mt-8 font-display italic text-[16px] md:text-[18px] max-w-[760px]"
            style={{ color: "var(--ink-secondary)" }}
          >
            "More certifications launching across healthcare, government, manufacturing, and
            specialized AI builder tracks. Demand-driven. Phase 2 begins Q3 2026."
          </p>
        </Reveal>
      </div>
    </section>
  );
}
